import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorService } from '@interacta-shared/data-access-error';
import {
    ButtonComponent,
    IconButtonComponent,
    ImageComponent,
    LoaderComponent,
    MenuDecoratorDirective,
    MenuDialogComponent,
    SeparatorComponent,
} from '@interacta-shared/ui';
import { AttachmentStorageType } from '@interacta-shared/util-caronte';
import { PostAttachmentService } from '@interacta/data-access-post';
import {
    AttachmentEntityType,
    emptyAttachmentsList,
    IAttachment,
    IListAttachments,
} from '@interacta/util-post';
import { AttachmentsSelectionListComponent } from '@modules/attachments/components/attachments-selection-list/attachments-selection-list.component';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, EMPTY } from 'rxjs';

type AISharedPickerEntityType = Extract<
    AttachmentEntityType,
    'post' | 'comment' | 'postFilePicker' | 'screenFilePicker'
>;

type AISharedPickerSection = {
    entityType: AISharedPickerEntityType;
    titleKey: string;
};

const PICKER_PAGE_SIZE = 5;

@Component({
    selector: 'interacta-ai-shared-attachment-picker',
    templateUrl: './ai-shared-attachment-picker.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AttachmentsSelectionListComponent,
        ButtonComponent,
        CdkOverlayOrigin,
        IconButtonComponent,
        ImageComponent,
        LoaderComponent,
        MenuDecoratorDirective,
        MenuDialogComponent,
        SeparatorComponent,
        TranslatePipe,
    ],
})
export class AISharedAttachmentPickerComponent {
    private readonly attachmentService = inject(PostAttachmentService);
    private readonly errorService = inject(ErrorService);
    private readonly destroyRef = inject(DestroyRef);

    readonly postId = input.required<number>();
    readonly disabled = input(false);
    readonly themeMode = input<string>();

    readonly attachmentSelected = output<IAttachment>();

    readonly isOpen = signal(false);
    readonly selected = signal<IAttachment | null>(null);

    private readonly lists = signal<
        Partial<Record<AISharedPickerEntityType, IListAttachments>>
    >({});

    /**
     * The workflow section is requested unconditionally: the BE already filters by
     * permission, and a section with no attachments is not rendered — so gating it
     * on the post's capability would only buy back one paginated call.
     */
    readonly sections: AISharedPickerSection[] = [
        { entityType: 'post', titleKey: 'AI.ATTACHMENTS_IN_POST' },
        {
            entityType: 'postFilePicker',
            titleKey: 'AI.ATTACHMENTS_IN_CUSTOM_FIELDS',
        },
        {
            entityType: 'screenFilePicker',
            titleKey: 'AI.ATTACHMENTS_IN_WORKFLOW',
        },
        { entityType: 'comment', titleKey: 'AI.ATTACHMENTS_IN_COMMENTS' },
    ];

    /**
     * Which sections have answered at least once. Tracked apart from `isLoading`
     * because a load-more sets that flag again on a section already answered, and
     * because the sections answer one by one: keying the initial loader off the
     * lists alone would uncover the empty state as soon as the first — often
     * empty — section lands, then flip back to the list when the next arrives.
     */
    private readonly answeredSections = signal<
        ReadonlySet<AISharedPickerEntityType>
    >(new Set());

    readonly isInitialLoading = computed(
        () => this.answeredSections().size < this.sections.length,
    );

    readonly hasAttachments = computed(() =>
        this.sections.some(
            ({ entityType }) => (this.lists()[entityType]?.totalCount ?? 0) > 0,
        ),
    );

    listOf(entityType: AISharedPickerEntityType): IListAttachments | undefined {
        return this.lists()[entityType];
    }

    open(): void {
        this.isOpen.set(true);
        this.selected.set(null);
        this.lists.set({});
        this.answeredSections.set(new Set());
        this.sections.forEach(({ entityType }) => this.fetch(entityType));
    }

    close(): void {
        this.isOpen.set(false);
        this.selected.set(null);
    }

    loadMore(entityType: AISharedPickerEntityType): void {
        const pageTokenInfo = this.listOf(entityType)?.pageTokenInfo;
        if (!pageTokenInfo || pageTokenInfo.tag === 'lastLoading') return;

        this.fetch(
            entityType,
            pageTokenInfo.tag === 'regularLoading'
                ? pageTokenInfo.nextPageToken
                : undefined,
        );
    }

    toggleSelection(attachment: IAttachment): void {
        this.selected.update((current) =>
            current?.id === attachment.id ? null : attachment,
        );
    }

    confirm(): void {
        const attachment = this.selected();
        if (!attachment) return;

        this.attachmentSelected.emit(attachment);
        this.close();
    }

    private fetch(
        entityType: AISharedPickerEntityType,
        nextPageToken?: string,
    ): void {
        this.lists.update((lists) => ({
            ...lists,
            [entityType]: {
                ...(lists[entityType] ?? emptyAttachmentsList()),
                isLoading: true,
            },
        }));

        this.attachmentService
            .loadPostAttachments(this.postId(), {
                nextPageToken,
                entityTypes: [entityType],
                types: [AttachmentStorageType.STORAGE],
                calculateTotalItemsCount: true,
                pageSize: PICKER_PAGE_SIZE,
                aiSupportedFilter: true,
            })
            .pipe(
                catchError((error: unknown) => {
                    this.errorService.handle(error);
                    this.markAnswered(entityType);
                    this.lists.update((lists) => ({
                        ...lists,
                        [entityType]: {
                            ...(lists[entityType] ?? emptyAttachmentsList()),
                            isLoading: false,
                            isError: true,
                        },
                    }));
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((page) => {
                this.markAnswered(entityType);
                this.lists.update((lists) => {
                    const current = lists[entityType];
                    return {
                        ...lists,
                        [entityType]: {
                            ...page,
                            attachments: nextPageToken
                                ? [
                                      ...(current?.attachments ?? []),
                                      ...page.attachments,
                                  ]
                                : page.attachments,
                        },
                    };
                });
            });
    }

    private markAnswered(entityType: AISharedPickerEntityType): void {
        this.answeredSections.update((answered) =>
            new Set(answered).add(entityType),
        );
    }
}
